import type { ScreenContext } from "@/types";

const SCREEN_REFERENCE_PATTERNS = [
  /\b(this|current)\s+(step|form|wizard)\b/i,
  /this\s+(chart|graph|table|metric|card|data|number|trend|value|figure|widget|screen|page|view|dashboard|list)/i,
  /the\s+(chart|graph|table|metric|card|data|number|trend|value|figure|widget|screen|page|view|dashboard|list)/i,
  /what\s+(does|do)\s+(this|these|it)\s+show/i,
  /(what|what\s+am)\s+(am\s+i|do\s+I|is\s+(shown|displayed|visible))\s+(looking\s+at|seeing|viewing|on\s+this)/i,
  /(what|how)\s+(is|am)\s+(shown|displayed|visible|laid\s+out)\s+on\s+(this|my)\s+(screen|page|view)/i,
  /(describe|summari[sz]e)\s+(this|the|my|current)\s+(current\s+)?(screen|page|view|dashboard)/i,
  /(what|which)\s+(content|data|widgets|charts|metrics|elements)\s+(is|are)\s+on\s+(this|my)\s+(screen|page)/i,
  /what\s+am\s+i\s+(on|looking\s+at)/i,
  /why\s+(is|are)\s+(this|that|it|the)\s+\w+\s+(so|very|high|low|increasing|decreasing|rising|falling)/i,
  /explain\s+(this|that|it|the)/i,
  /on\s+this\s+page/i,
  /the\s+current\s+(data|view|page)/i,
  /these\s+numbers/i,
  /above\s+(chart|graph|table|data|metric)/i,
  /visible\s+(on|in)/i,
  /what\s+does\s+the\s+\w+\s+(mean|show|indicate|represent)/i,
  /how\s+(is|are)\s+(this|that|the)\s+\w+\s+(calculated|computed|derived)/i,
  /what\s+is\s+the\s+\w+\s+(trend|pattern|breakdown)/i,
  /summari[sz]e\s+(what\s+)?(i\s+)?see/i,
  /\b(rows?|records?)\s+(here|shown|visible|above)\b/i,
  /can\s+you\s+(explain|describe|analyze|analyse)\s+(this|that|the)/i,
  /tell\s+me\s+about\s+(this|that|the)\s+(chart|graph|table|metric|data|number)/i,
  /what\s+insights/i,
  /how\s+does\s+this\s+compare/i,
];

const STOP_WORDS = new Set([
  "about",
  "above",
  "chart",
  "current",
  "data",
  "from",
  "page",
  "screen",
  "table",
  "that",
  "this",
  "view",
  "with",
]);

export function detectScreenReference(
  input: string,
  context?: ScreenContext,
): boolean {
  if (SCREEN_REFERENCE_PATTERNS.some((p) => p.test(input))) return true;
  if (!context) return false;
  if (
    context.activeView?.startsWith("emission-") &&
    /\b(log|record|enter)\s+(?:my\s+|the\s+|an?\s+)?\w+/i.test(input)
  )
    return true;
  if (context.formFields?.length && detectFormReference(input)) return true;

  const isQuestionOrAction =
    /\b(what|which|who|where|when|why|how|is|are|do|does|can|could|compare|summari[sz]e|explain|show|list|tell|help|review|analy[sz]e|highest|lowest|most|least|selected)\b/i.test(
      input,
    );
  if (!isQuestionOrAction) return false;

  const contextTerms = [
    context.pageTitle,
    context.summary,
    context.activeView,
    ...(context.formSections ?? []).map((section) => section.label),
    ...Object.keys(context.keyMetrics ?? {}),
    ...context.elements,
    ...(context.visibleRecords ?? []).flatMap((record) => [
      record.table,
      ...Object.keys(record.fields),
      ...Object.values(record.fields),
    ]),
    ...(context.chartSummaries ?? []),
    ...(context.formFields ?? []).flatMap((field) => [
      field.label,
      field.value ?? "",
    ]),
  ]
    .join(" ")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((term) => term.length >= 4 && !STOP_WORDS.has(term));

  const message = input.toLowerCase();
  return contextTerms.some((term) => message.includes(term));
}

const FORM_REFERENCE_PATTERNS = [
  /which\s+(emission\s+)?(categories?|scopes?|sources?|methods?|databases?|factors?)/i,
  /(what|which)\s+(emission\s+)?(category|scope|source|method|database)\s+(should|do|would)\s+i\s+(use|pick|select|choose|add)/i,
  /(how|help)\s+(do\s+i|should\s+i)\s+(set\s+up|fill|complete|configure|choose|select|add)\s+(this\s+)?(form|emissions?|categories?|sources?|methods?)/i,
  /(help|assist|guide)\s+me\s+(fill|complete|set\s+up|configure)/i,
  /what\s+(categories?|sources?|methods?)\s+(do|does|would)\s+(i|my\s+facility|this\s+facility)/i,
  /\b(scope\s*1|scope\s*2|scope\s*3)\b/i,
  /\b(DEFRA|EPA|GHG|IPCC)\b/i,
  /\b(emission\s+factor(s)?|emission\s+categor(y|ies)|emission\s+source(s)?|log\s+emission\s+against)\b/i,
  /which\s+(one|of\s+these)\s+(should|do)\s+i\s+(pick|select|choose)/i,
  /\b(preferences|basic\s+information)\s+step\b/i,
  /\b(should\s+i|do\s+i\s+need)\s+(to\s+)?(select|add|include|use)\b/i,
  /next\s+step/i,
  // Questions about the form's fields/inputs themselves. Without these the
  // consent banner never appeared for "what are the fields in this form", so
  // the assistant got no form context and asked for a screenshot instead.
  /\b(each|every|all)\s+(and\s+every\s+)?(of\s+the\s+)?(fields?|inputs?|questions?|inputs)\b/i,
  /\b(list|show|explain|describe|tell\s+me)\s+(me\s+)?(all\s+|the\s+|every\s+)?(fields?|inputs?|questions?)\b/i,
  /\bwhat\s+(fields?|inputs?|questions?)\b/i,
  /\b(fields?|inputs?)\s+(of|in|on|for)\s+(this|the|my)\s+(form|page|wizard|screen|facility)/i,
  /\b(why|what\s+is|what's|for\s+what|purpose\s+of)\s+(the\s+|this\s+|each\s+|every\s+)?(fields?|inputs?|column|columns)\b/i,
  /\b(meaning|relevance|significance)\s+of\s+(each|every|the|all)\b/i,
  /\bform\s+(fields?|schema|structure|questions?)\b/i,
  /\bwhat\s+does\s+(this|the)\s+form\s+(ask|require|contain|include|need)/i,
  /\b(mandatory|required)\s+(fields?|inputs?)\b/i,
  /\bsingle[\s-]?select|multi[\s-]?select|dropdown|can\s+i\s+select\s+multiple/i,
];

/**
 * Detects whether the message is asking the AI for help with the form the
 * user is currently filling (the facility wizard), so live form context can
 * be attached without a separate confirmation prompt.
 */
export function detectFormReference(input: string): boolean {
  return FORM_REFERENCE_PATTERNS.some((p) => p.test(input));
}
